'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LayoutDashboard,
  CalendarCheck,
  Users,
  Car,
  KeyRound,
  Wrench,
  FileBarChart,
  Settings,
  Gauge,
  Search,
  ShieldCheck,
  Tag,
  Wallet,
  Siren,
  Building2,
  UserRound,
} from 'lucide-react';
import { CommandPalette } from '@/components/dashboard/CommandPalette';
import { AccountMenu, ThemeToggle } from '@/components/dashboard/Header';
import { OwnProfile } from '@/components/dashboard/OwnProfile';
import { LogoutModal } from '@/components/dashboard/LogoutModal';
import { useDashboardTheme } from '@/components/dashboard/useDashboardTheme';
import type { CommandEntry, DashboardRole } from '@/components/dashboard/types';
import { ImpersonationBanner } from '@/components/platform/ScopeBanner';
import type { Booking, FleetExpense, RentalClient, StaffRole, Vehicle, VehicleStatus } from '@/types';
import { DEFAULT_AGENCY, officeLocations, type AgencyProfile } from '@/lib/agency';
import { api, ApiError } from '@/lib/api';
import { parseBookingPage } from '@/lib/booking/laravel-mirror';
import { parseClientPage } from '@/lib/clients/client-catalog';
import { useVehicleOptions, type VehicleOption } from '@/lib/desk/summary';
import { useInvalidateTenantResource } from '@/lib/query/scope';
import { useTenantQuery } from '@/lib/query/useTenantQuery';
import { DESK_COMMAND_ACTIONS } from '@/lib/desk/command-palette';
import { describeDeskImport, parseDeskImport, type DeskImportBundle } from '@/lib/desk/import-bundle';
import type { ListCap } from '@/lib/desk/list-caps';
import { MOROCCAN_LOCATIONS } from '@/lib/morocco';
import { DashboardModule } from './DashboardModule';
import BookingsModule from './BookingsModule';
import ClientsModule from './ClientsModule';
import { FleetModule } from './FleetModule';
import { HandoverModule } from './HandoverModule';
import { ContractsPanel } from './ContractsPanel';
import { FinanceModule } from './FinanceModule';
import { SettingsModule } from './SettingsModule';
import { AgencyModule } from './AgencyModule';
import { TariffsModule, type TariffRule, type DiscountTier, type ExtraItem } from './TariffsModule';
import { FinesModule, type FineRecord } from './FinesModule';
import { CashModule, type CashMovement } from './CashModule';
import { DeskToastProvider, useDeskNotify } from './desk-feedback';
import { ListCapBanner } from './ListCapBanner';
import { NotificationsBell } from './NotificationsBell';
import { StaffModule } from './StaffModule';
import { FeatureLocked } from './FeatureLocked';
import { can, featureForSection, type Entitlements } from '@/lib/entitlements';

export interface AdminTariffs {
  rules: TariffRule[];
  tiers: DiscountTier[];
  extras: ExtraItem[];
}

interface AdminFleetViewProps {
  initialVehicles: Vehicle[];
  initialBookings: Booking[];
  initialClients: RentalClient[];
  initialExpenses: FleetExpense[];
  initialAgency: AgencyProfile | null;
  initialTariffs: AdminTariffs;
  initialFines: FineRecord[];
  initialMovements: CashMovement[];
  sessionRole: string;
  operatorName?: string;
  operatorEmail?: string;
  fleetUnavailable?: boolean;
  impersonation?: { agencyName: string } | null;
  listCaps?: ListCap[];
  jsonImportAllowed?: boolean;
  entitlements?: Entitlements | null;
  initialSection?: string;
  onNavigateHome?: () => void;
  // Legacy props (website used to embed the console with these). Kept optional
  // for backward compatibility — they are ignored, the console owns its state.
  vehicles?: Vehicle[];
  bookings?: Booking[];
  onAddVehicle?: (newVehicle: Vehicle) => void;
  onUpdateVehicle?: (v: Vehicle) => void;
  onUpdateVehicleStatus?: (id: string, status: VehicleStatus) => void;
  onDeleteVehicle?: (id: string) => void;
  onAddBooking?: (b: Booking) => void;
  onUpdateBooking?: (b: Booking) => void;
  onUpdateBookingStatus?: (id: string, status: Booking['status']) => void;
  onReplaceVehicles?: (list: Vehicle[]) => void;
  onReplaceBookings?: (list: Booking[]) => void;
}

type AdminSidebarSection =
  | 'dashboard'
  | 'bookings'
  | 'customers'
  | 'fleet'
  | 'handover'
  | 'maintenance'
  | 'tarifs'
  | 'caisse'
  | 'amendes'
  | 'analytics'
  | 'agence'
  | 'staff'
  | 'profile'
  | 'settings';

const TITLES: Record<AdminSidebarSection, string> = {
  dashboard: 'Operations Overview',
  bookings: 'Customer Reservations',
  customers: 'Renter Accounts CRM',
  fleet: 'Fleet Management',
  handover: 'Contracts, Handovers & Returns',
  maintenance: 'Maintenance & Repair Bays',
  tarifs: 'Tarifs & Extras',
  caisse: 'Caisse journalière',
  amendes: 'Contraventions',
  analytics: 'Fleet Revenue & Analytics',
  agence: 'Mon Agence',
  staff: 'Staff',
  profile: 'My profile',
  settings: 'System Preferences & Roles',
};

// Debounced bulk-PUT sync for server-backed collections. The first payload is
// the server copy, so it is not written back. Later renders that rebuild the
// same JSON (a new object, the same rows) are not a change. A failed save is
// reported once and retried only when the payload changes again.
function selectorVehicle(option: VehicleOption): Vehicle {
  return {
    id: option.id,
    name: option.name,
    brand: option.brand,
    model: option.model,
    category: 'Economy',
    pricePerDay: option.pricePerDay,
    seats: 5,
    transmission: 'Manual',
    bags: 2,
    fuelType: 'Diesel',
    mpg: '',
    rating: 0,
    reviewsCount: 0,
    licensePlate: option.licensePlate,
    status: option.status === 'rented' || option.status === 'maintenance' || option.status === 'reserved' ? option.status : 'available',
    mileage: 0,
    features: [],
    imageType: '',
    colorName: '',
    year: new Date().getFullYear(),
    location: option.location,
  };
}

function useBulkSync<T>(
  label: string,
  path: string,
  body: T,
  onError?: (err: ApiError) => void,
  onFail?: (message: string) => void,
) {
  const serialized = JSON.stringify(body);
  const baseline = useRef<string | null>(null);
  const onErrorRef = useRef(onError);
  const onFailRef = useRef(onFail);

  useEffect(() => {
    onErrorRef.current = onError;
    onFailRef.current = onFail;
  }, [onError, onFail]);

  useEffect(() => {
    if (baseline.current === null) {
      baseline.current = serialized;
      return;
    }
    if (baseline.current === serialized) return;

    const payload = serialized;
    const timer = setTimeout(() => {
      baseline.current = payload;
      api(path, { method: 'PUT', body: payload }).catch((err: unknown) => {
        if (err instanceof ApiError && onErrorRef.current) {
          onErrorRef.current(err);
          return;
        }
        const message = err instanceof Error ? err.message : 'Unknown error';
        onFailRef.current?.(`Save failed (${label}): ${message} — will retry on the next change.`);
      });
    }, 800);

    return () => clearTimeout(timer);
  }, [label, path, serialized]);
}

export function AdminFleetView({
  initialVehicles,
  initialBookings,
  initialClients,
  initialExpenses,
  initialAgency,
  initialTariffs,
  initialFines,
  initialMovements,
  sessionRole,
  operatorName = '',
  operatorEmail = '',
  fleetUnavailable = false,
  impersonation = null,
  listCaps = [],
  jsonImportAllowed = false,
  entitlements = null,
  initialSection,
  onNavigateHome,
}: AdminFleetViewProps) {
  const [activeSection, setActiveSection] = useState<AdminSidebarSection>(
    isDeskSection(initialSection ?? '') ? initialSection as AdminSidebarSection : 'dashboard',
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [createRequest, setCreateRequest] = useState<{ kind: 'booking' | 'client'; token: number } | null>(null);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const { theme, toggleTheme } = useDashboardTheme();
  const accountRole: DashboardRole = sessionRole === 'super_admin' ? 'super_admin' : 'front_desk';

  // Role comes from the server session — no local switcher.
  const role: StaffRole = accountRole;

  const sectionAllowed = (section: AdminSidebarSection): boolean => {
    const feature = featureForSection(section);
    return feature === null || can(entitlements, feature);
  };
  const lockedFeature = featureForSection(activeSection);
  const locked = lockedFeature !== null && !can(entitlements, lockedFeature);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen(true);
      }
      if (event.key === 'Escape') setPaletteOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Server-owned state, initialized from the server component's props.
  const [vehicles, setVehicles] = useState<Vehicle[]>(initialVehicles);
  const [bookings, setBookings] = useState<Booking[]>(initialBookings);
  const [agency, setAgencyState] = useState<AgencyProfile>({
    ...DEFAULT_AGENCY,
    ...(initialAgency ?? {}),
  });
  const [clients, setClients] = useState<RentalClient[]>(initialClients);
  const [expenses, setExpenses] = useState<FleetExpense[]>(initialExpenses);
  const [tariffRules, setTariffRules] = useState<TariffRule[]>(initialTariffs.rules);
  const [discountTiers, setDiscountTiers] = useState<DiscountTier[]>(initialTariffs.tiers);
  const [extras, setExtras] = useState<ExtraItem[]>(initialTariffs.extras);
  const [fines, setFines] = useState<FineRecord[]>(initialFines);
  const [movements, setMovements] = useState<CashMovement[]>(initialMovements);
  const { toast, notify } = useDeskNotify();
  const invalidate = useInvalidateTenantResource();
  const vehicleOptions = useVehicleOptions();
  const selectorVehicles = useMemo(
    () => (vehicleOptions.data?.data ?? []).map(selectorVehicle),
    [vehicleOptions.data],
  );
  const openBookings = useTenantQuery(
    ['bookings', 'open'],
    '/api/bookings?open=1&perPage=50',
    parseBookingPage,
  );
  const handoverBookings = (openBookings.data?.data ?? []) as Booking[];
  const clientDirectory = useTenantQuery(['clients', 'directory'], '/api/clients?perPage=50', parseClientPage);
  const directoryClients = (clientDirectory.data?.data ?? []) as RentalClient[];
  const reportSaveFailure = useCallback((message: string) => notify(message, 'error'), [notify]);

  const tariffsBody = useMemo(
    () => ({ rules: tariffRules, tiers: discountTiers, extras }),
    [tariffRules, discountTiers, extras]
  );
  useBulkSync('tariffs', '/api/tariffs', tariffsBody, undefined, reportSaveFailure);
  const handleAgencyError = useCallback((err: ApiError) => {
    if (err.status === 403) {
      notify('The agency profile could not be saved. Reloading the server copy.', 'error');
      // Roll back to the server copy. Use the staff payload so the legal
      // identifiers and contract footer are not replaced by public defaults.
      api<AgencyProfile>('/api/staff/agency')
        .then((fresh) => setAgencyState({ ...DEFAULT_AGENCY, ...fresh }))
        .catch(() => {});
    } else {
      notify(`Save failed (agency): ${err.message} — will retry on the next change.`, 'error');
    }
  }, [notify]);
  useBulkSync('agency', '/api/agency', agency, handleAgencyError);

  const setAgency = (a: AgencyProfile) => setAgencyState(a);

  // ---- Vehicles: per-item API mutations with optimistic rollback ----
  const handleAddVehicle = async (newVehicle: Vehicle): Promise<Vehicle | null> => {
    const prev = vehicles;
    setVehicles([newVehicle, ...prev]);
    try {
      const created = await api<Vehicle>('/api/vehicles', {
        method: 'POST',
        body: JSON.stringify(newVehicle),
      });
      // Reconcile the optimistic entry with the server record.
      setVehicles((cur) =>
        created.id !== newVehicle.id
          ? [created, ...cur.filter((v) => v.id !== newVehicle.id)]
          : cur.map((v) => (v.id === newVehicle.id ? created : v))
      );
      await invalidate('vehicles');
      await invalidate('vehicle-options');
      await invalidate('desk-summary');
      return created;
    } catch (err) {
      setVehicles(prev);
      notify(`Could not create vehicle: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
      return null;
    }
  };

  const handleUpdateVehicle = async (updated: Vehicle) => {
    const prev = vehicles;
    setVehicles(prev.map((v) => (v.id === updated.id ? updated : v)));
    try {
      await api(`/api/vehicles/${updated.id}`, { method: 'PATCH', body: JSON.stringify(updated) });
      await invalidate('vehicles');
      await invalidate('vehicle-options');
      await invalidate('desk-summary');
    } catch (err) {
      setVehicles(prev);
      notify(`Could not update vehicle: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
    }
  };

  const handleUpdateVehicleStatus = async (id: string, status: VehicleStatus) => {
    const prev = vehicles;
    setVehicles(prev.map((v) => (v.id === id ? { ...v, status } : v)));
    try {
      await api(`/api/vehicles/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) });
      await invalidate('vehicles');
      await invalidate('desk-summary');
    } catch (err) {
      setVehicles(prev);
      notify(`Could not update vehicle status: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
    }
  };

  const handleDeleteVehicleInner = async (id: string): Promise<true | string> => {
    const prev = vehicles;
    setVehicles(prev.filter((v) => v.id !== id));
    try {
      await api(`/api/vehicles/${id}`, { method: 'DELETE' });
      await invalidate('vehicles');
      await invalidate('vehicle-options');
      await invalidate('desk-summary');
      return true;
    } catch (err) {
      setVehicles(prev);
      return err instanceof Error ? err.message : 'Could not delete this vehicle.';
    }
  };

  // ---- Bookings: per-item API mutations with optimistic rollback ----
  const handleAddBooking = async (b: Booking) => {
    const prev = bookings;
    setBookings([b, ...prev]);
    try {
      const created = await api<Booking>('/api/bookings', { method: 'POST', body: JSON.stringify(b) });
      setBookings((cur) =>
        created.id !== b.id ? [created, ...cur.filter((x) => x.id !== b.id)] : cur.map((x) => (x.id === b.id ? created : x))
      );
      await invalidate('bookings');
      await invalidate('desk-summary');
      return true;
    } catch (err) {
      setBookings(prev);
      if (err instanceof ApiError && err.status === 409) {
        notify('Booking conflict: this vehicle is already booked for those dates.', 'error');
      } else {
        notify(`Could not create booking: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
      }
      return false;
    }
  };

  const handleUpdateBooking = async (b: Booking): Promise<boolean> => {
    try {
      const saved = await api<Booking>(`/api/bookings/${b.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          vehicleId: b.vehicleId,
          customerName: b.customerName,
          customerEmail: b.customerEmail,
          customerPhone: b.customerPhone,
          driverLicense: b.driverLicense,
          pickupLocation: b.pickupLocation,
          dropoffLocation: b.dropoffLocation,
          pickupDate: b.pickupDate,
          pickupTime: b.pickupTime,
          dropoffDate: b.dropoffDate,
          dropoffTime: b.dropoffTime,
          protectionPlan: b.protectionPlan,
          selectedAddOns: b.selectedAddOns,
          promoCode: b.promoCode,
          avanceAmount: b.avanceAmount,
          cautionAmount: b.cautionAmount,
          cautionType: b.cautionType,
          paymentMethod: b.paymentMethod,
          notes: b.notes,
          pickupMileage: b.pickupMileage ?? null,
          returnMileage: b.returnMileage ?? null,
          pickupFuel: b.pickupFuel ?? '',
          returnFuel: b.returnFuel ?? '',
          returnCharges: b.returnCharges ?? [],
          cautionStatus: b.cautionStatus ?? 'held',
          cautionRetained: b.cautionRetained ?? 0,
          pickupPhotos: b.pickupPhotos ?? [],
          returnPhotos: b.returnPhotos ?? [],
          idDocumentPhoto: b.idDocumentPhoto ?? '',
        }),
      });
      setBookings((current) => current.map((row) => (row.id === b.id ? { ...row, ...saved } : row)));
      await invalidate('bookings');
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const conflictId = err.body && typeof err.body === 'object' && 'conflictId' in err.body
          ? String((err.body as { conflictId?: unknown }).conflictId ?? '')
          : '';
        notify(conflictId ? `Already booked (${conflictId}).` : 'This vehicle is already booked for those dates.', 'error');
      } else if (err instanceof ApiError && err.status === 413) {
        notify('Booking photos are too large. Use smaller images.', 'error');
      } else {
        notify(`Could not update booking: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
      }
      return false;
    }
  };

  const handleUpdateBookingStatus = async (id: string, status: Booking['status']) => {
    const prev = bookings;
    setBookings(prev.map((x) => (x.id === id ? { ...x, status } : x)));
    try {
      await api(`/api/bookings/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) });
      await invalidate('bookings');
      await invalidate('desk-summary');
    } catch (err) {
      setBookings(prev);
      notify(`Could not update booking status: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
    }
  };

  const applyBookingStatus = async (ids: string[], status: Booking['status']) => {
    const idSet = new Set(ids);
    const prev = bookings;
    setBookings(prev.map((booking) => (idSet.has(booking.id) ? { ...booking, status } : booking)));
    const results = await Promise.allSettled(ids.map((id) => api(`/api/bookings/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) })));
    if (results.some((result) => result.status === 'rejected')) {
      setBookings(prev);
      return false;
    }
    await invalidate('bookings');
    await invalidate('desk-summary');
    return true;
  };

  const applyVehicleStatus = async (ids: string[], status: VehicleStatus) => {
    const idSet = new Set(ids);
    const prev = vehicles;
    setVehicles(prev.map((vehicle) => (idSet.has(vehicle.id) ? { ...vehicle, status } : vehicle)));
    const results = await Promise.allSettled(ids.map((id) => api(`/api/vehicles/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) })));
    if (results.some((result) => result.status === 'rejected')) {
      setVehicles(prev);
      return false;
    }
    await invalidate('vehicles');
    await invalidate('desk-summary');
    return true;
  };

  const canDelete = role === 'super_admin';
  const canViewProfits = role === 'super_admin';
  const locations = officeLocations(agency, MOROCCAN_LOCATIONS);
  const deskQuery = searchQuery.trim().toLowerCase();
  const searchedBookings = deskQuery === ''
    ? handoverBookings
    : handoverBookings.filter((booking) => `${booking.id} ${booking.customerName} ${booking.vehicleName} ${booking.pickupLocation}`.toLowerCase().includes(deskQuery));

  const handleDeleteVehicle = (id: string): Promise<true | string> => {
    if (!canDelete) return Promise.resolve('Front desk cannot delete vehicles.');
    return handleDeleteVehicleInner(id);
  };

  const handleResetDemo = () => {
    // Data is server-owned now: just drop legacy local caches and reload.
    try {
      [
        'fleetava_clients',
        'fleetava_expenses',
        'fleetava_vehicles',
        'fleetava_bookings',
        'fleetava_tariff_rules',
        'fleetava_discount_tiers',
        'fleetava_extras',
        'fleetava_fines',
        'fleetava_cash',
        'fleetava_agency',
        'fleetava_role',
      ].forEach((k) => localStorage.removeItem(k));
    } catch {}
    window.location.reload();
  };

  const backupData = {
    version: 1,
    exportedAt: new Date().toISOString(),
    agency,
    clients,
    expenses,
    tariffRules,
    discountTiers,
    extras,
    fines,
    movements,
    vehicles,
    bookings,
  };

  const handleImport = (data: unknown): { ok: boolean; message: string } => {
    const parsed = parseDeskImport(data);
    if (!parsed.ok) return parsed;
    const bundle: DeskImportBundle = parsed.bundle;
    if (bundle.agency) {
      const { pickupLocations, ...profile } = bundle.agency;
      setAgencyState((current) => ({
        ...current,
        ...profile,
        pickupLocations: pickupLocations ?? current.pickupLocations,
        whatsappTemplate: bundle.agency?.whatsappTemplate ?? current.whatsappTemplate,
      }));
    }
    if (bundle.clients) setClients(bundle.clients);
    if (bundle.expenses) setExpenses(bundle.expenses);
    if (bundle.tariffRules) setTariffRules(bundle.tariffRules);
    if (bundle.discountTiers) setDiscountTiers(bundle.discountTiers);
    if (bundle.extras) setExtras(bundle.extras);
    if (bundle.fines) setFines(bundle.fines);
    if (bundle.movements) setMovements(bundle.movements);
    return { ok: true, message: `Office data replaced: ${describeDeskImport(bundle)}.` };
  };

  const commandEntries: CommandEntry[] = [
    ...DESK_COMMAND_ACTIONS,
    { id: 'dashboard', label: 'Dashboard', group: 'Desk', href: '#dashboard' },
    { id: 'fleet', label: 'Fleet', group: 'Desk', href: '#fleet' },
    { id: 'bookings', label: 'Bookings', group: 'Desk', href: '#bookings' },
    { id: 'customers', label: 'Customers', group: 'Desk', href: '#customers' },
    { id: 'handover', label: 'Contracts', group: 'Desk', href: '#handover' },
    { id: 'maintenance', label: 'Maintenance', group: 'Desk', href: '#maintenance' },
    { id: 'tarifs', label: 'Tariffs', group: 'Desk', href: '#tarifs' },
    { id: 'caisse', label: 'Cash', group: 'Desk', href: '#caisse' },
    { id: 'amendes', label: 'Fines', group: 'Desk', href: '#amendes' },
    { id: 'analytics', label: 'Reports', group: 'Desk', href: '#analytics' },
    { id: 'agence', label: 'Agency profile', group: 'Desk', href: '#agence' },
    ...(role === 'super_admin' ? [{ id: 'staff', label: 'Staff', group: 'Desk', href: '#staff' }] : []),
    { id: 'profile', label: 'My profile', group: 'Desk', href: '#profile' },
    { id: 'settings', label: 'Settings', group: 'Desk', href: '#settings' },
    ...vehicles.slice(0, 40).map((vehicle) => ({
      id: `vehicle-${vehicle.id}`,
      label: `${vehicle.name} · ${vehicle.licensePlate}`,
      group: 'Vehicles',
      href: `#vehicle-${vehicle.id}`,
    })),
  ].filter((entry) => {
    const section = entry.href.startsWith('#') ? entry.href.slice(1) : '';
    return !isDeskSection(section) || sectionAllowed(section);
  });

  function openCommand(href: string) {
    if (href === '#action-new-booking') {
      setCreateRequest((current) => ({ kind: 'booking', token: (current?.token ?? 0) + 1 }));
      setActiveSection('bookings');
      return;
    }
    if (href === '#action-new-client') {
      setCreateRequest((current) => ({ kind: 'client', token: (current?.token ?? 0) + 1 }));
      setActiveSection('customers');
      return;
    }
    if (href.startsWith('#vehicle-')) {
      const vehicleId = href.slice('#vehicle-'.length);
      const vehicle = vehicles.find((item) => item.id === vehicleId);
      setSearchQuery(vehicle?.licensePlate ?? '');
      setActiveSection('fleet');
      return;
    }
    const section = href.slice(1);
    if (isDeskSection(section)) setActiveSection(section);
  }

  const navItem = (section: AdminSidebarSection, icon: React.ReactNode, label: string, badge?: React.ReactNode) => {
    if (!sectionAllowed(section)) return null;
    return (
    <button
      type="button"
      onClick={() => setActiveSection(section)}
      className={`flex w-auto shrink-0 items-center gap-3.5 whitespace-nowrap px-3.5 py-3 rounded-2xl transition-colors cursor-pointer text-xs font-semibold lg:w-full ${
        activeSection === section
          ? 'bg-brand/15 text-ink font-bold'
          : 'text-muted hover:text-ink hover:bg-card'
      }`}
    >
      {icon}
      <span className="truncate">{label}</span>
      {badge}
    </button>
    );
  };

  return (
    <DeskToastProvider notify={notify} toast={toast}>
    <div data-theme={theme} className="dashboard-root flex min-h-dvh w-full flex-col bg-shell font-sans text-ink antialiased">
      {fleetUnavailable && (
        <p className="bg-danger/10 px-4 py-2 text-center text-xs font-semibold text-danger">
          The rental service did not respond. The lists below are empty because the fleet could not be loaded.
        </p>
      )}
      {impersonation && <ImpersonationBanner agencyName={impersonation.agencyName} />}
      <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-shell lg:flex-row">
        <aside className="flex w-full shrink-0 flex-col justify-between border-b border-line bg-shell p-4 sm:p-6 lg:w-64 lg:border-b-0 lg:border-r lg:p-6">
          <div>
            <div className="flex items-center justify-between mb-1 pb-2">
              <div onClick={onNavigateHome} className="flex min-w-0 items-center gap-2.5 cursor-pointer group">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand text-slate-950 shadow-sm transition-transform group-hover:scale-105">
                  <Gauge className="w-5 h-5" />
                </div>
                <span className="truncate text-sm font-semibold tracking-tight text-ink">fleetava</span>
              </div>
              {onNavigateHome && (
                <button
                  type="button"
                  onClick={onNavigateHome}
                  className="shrink-0 text-[11px] font-semibold text-muted hover:text-ink"
                >
                  Exit ↗
                </button>
              )}
            </div>
            <p className="mb-2 truncate px-1 text-[11px] font-semibold text-muted" title={agency.name}>{agency.name}</p>
            {entitlements && (
              <div className="mb-3 rounded-2xl border border-line bg-card px-3 py-2 lg:mb-5">
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted">Subscription</p>
                <p className="truncate text-xs font-bold text-ink">{entitlements.plan.name}</p>
                {entitlements.extraKeys.length > 0 && (
                  <p className="mt-0.5 text-[10px] font-semibold text-brand">
                    +{entitlements.extraKeys.length} extra feature{entitlements.extraKeys.length === 1 ? '' : 's'}
                  </p>
                )}
              </div>
            )}

            <nav className="flex gap-1.5 overflow-x-auto pb-2 -mx-1 px-1 lg:mx-0 lg:block lg:space-y-1 lg:overflow-visible lg:p-0">
              {navItem('dashboard', <LayoutDashboard className="w-4 h-4 shrink-0" />, 'Dashboard')}
              {navItem(
                'bookings',
                <CalendarCheck className="w-4 h-4 shrink-0" />,
                'Bookings / Orders',
                bookings.length > 0 ? (
                  <span className="ml-auto rounded-full bg-brand/10 px-2 py-0.5 text-[10px] font-bold tabular-nums text-brand">
                    {bookings.length}
                  </span>
                ) : undefined
              )}
              {navItem('customers', <Users className="w-4 h-4 shrink-0" />, 'Customers CRM')}
              {navItem(
                'fleet',
                <Car className="w-4 h-4 shrink-0" />,
                'Fleet Vehicles',
                <span className="ml-auto text-[11px] font-mono font-bold tabular-nums text-muted">{vehicles.length}</span>
              )}
              {navItem('handover', <KeyRound className="w-4 h-4 shrink-0" />, 'Contracts & Handovers')}
              {navItem('maintenance', <Wrench className="w-4 h-4 shrink-0" />, 'Maintenance')}
              {navItem('tarifs', <Tag className="w-4 h-4 shrink-0" />, 'Tarifs & Extras')}
              {navItem('caisse', <Wallet className="w-4 h-4 shrink-0" />, 'Caisse')}
              {navItem('amendes', <Siren className="w-4 h-4 shrink-0" />, 'Contraventions')}
              {navItem('analytics', <FileBarChart className="w-4 h-4 shrink-0" />, 'Reports & Revenue')}
              {navItem('agence', <Building2 className="w-4 h-4 shrink-0" />, 'Mon Agence')}
              {role === 'super_admin' && navItem('staff', <ShieldCheck className="w-4 h-4 shrink-0" />, 'Staff')}
              {navItem('profile', <UserRound className="w-4 h-4 shrink-0" />, 'My profile')}
              {navItem('settings', <Settings className="w-4 h-4 shrink-0" />, 'System Settings')}
            </nav>

            {/* Role badge (session-managed — no switcher) */}
            <div className="mt-4 hidden items-center gap-2.5 rounded-2xl border border-line bg-card p-3 sm:flex lg:mt-6">
              <ShieldCheck className="h-4 w-4 shrink-0 text-muted" />
              <div className="min-w-0">
                <div className="type-table-header truncate text-muted">Signed in as</div>
                <div className="truncate text-xs font-bold text-ink">
                  {deskRoleLabel(role, Boolean(impersonation))}
                </div>
              </div>
            </div>
            {/* Compact role line for phones */}
            <p className="mt-3 truncate px-1 text-[11px] font-semibold text-muted sm:hidden">
              {deskRoleLabel(role, Boolean(impersonation))}
            </p>
          </div>

          <div className="mt-4 hidden rounded-3xl border border-line bg-card p-4 text-center lg:mt-8 lg:block">
            <h4 className="text-xs font-extrabold leading-tight text-ink">
              {agency.shortName} — {agency.city}
            </h4>
            <p className="mb-3 mt-1 text-[10px] text-muted">Airport + City Center dispatch</p>
            <button
              type="button"
              onClick={() => setActiveSection('bookings')}
              className="w-full cursor-pointer rounded-xl bg-brand py-2 text-[11px] font-bold uppercase tracking-wider text-slate-950 transition-colors hover:brightness-110"
            >
              + New booking
            </button>
          </div>
        </aside>

        {/* 2. MAIN CONTENT AREA */}
        <main className="flex min-w-0 grow flex-col overflow-y-auto bg-shell p-4 sm:p-6 lg:p-10">
          <header className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
            <div className="flex min-w-0 items-center gap-2">
              <h1 className="type-section truncate text-ink">
                {TITLES[activeSection]}
              </h1>
              {entitlements && (
                <span
                  className="shrink-0 rounded-full border border-line bg-card px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-muted"
                  title={`Subscription: ${entitlements.plan.name}`}
                >
                  {entitlements.plan.name}
                  {entitlements.extraKeys.length > 0 ? ` +${entitlements.extraKeys.length}` : ''}
                </span>
              )}
            </div>
            <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 lg:w-auto">
              <div className="relative w-full sm:min-w-[220px] sm:flex-1 lg:w-80 lg:flex-none">
                <Search className="w-4 h-4 text-muted absolute left-4 top-3.5" />
                <input
                  type="text"
                  placeholder="Search vehicle, plate, renter, hub..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-2xl border border-line bg-card py-2.5 pl-10 pr-4 text-xs text-ink transition-colors placeholder:text-muted focus:border-brand focus:outline-hidden focus:ring-2 focus:ring-brand/20"
                />
              </div>
              <div className="flex items-center gap-3 sm:gap-4">
                <button
                  type="button"
                  onClick={() => setPaletteOpen(true)}
                  className="inline-flex items-center gap-2 rounded-2xl border border-line bg-card px-3 py-2 text-xs font-semibold text-muted"
                >
                  <Search size={14} />
                  Ctrl K
                </button>
                <ThemeToggle theme={theme} onToggle={toggleTheme} />
                <NotificationsBell
                  vehicles={vehicles}
                  bookings={bookings}
                  onGoto={(s) => setActiveSection(s as AdminSidebarSection)}
                  currency={agency.currency}
                />
                <AccountMenu
                  user={{ name: operatorName, email: operatorEmail, role: accountRole }}
                  onSignOut={() => setLogoutOpen(true)}
                  onEditProfile={() => setActiveSection('profile')}
                />
              </div>
            </div>
          </header>

          <ListCapBanner caps={listCaps} />

          {locked && lockedFeature ? (
            <FeatureLocked feature={lockedFeature} entitlements={entitlements} onBack={() => setActiveSection('dashboard')} />
          ) : (
            <>
          {activeSection === 'dashboard' && (
            <DashboardModule
              vehicles={vehicles}
              bookings={bookings}
              expenses={expenses}
              onGoto={(s) => setActiveSection(s as AdminSidebarSection)}
              currency={agency.currency}
              headerQuery={searchQuery}
              canViewProfits={canViewProfits}
              agencyName={agency.shortName || agency.name}
              city={agency.city}
              onNewBooking={() => {
                setCreateRequest((current) => ({ kind: 'booking', token: (current?.token ?? 0) + 1 }));
                setActiveSection('bookings');
              }}
            />
          )}

          {activeSection === 'bookings' && (
            <BookingsModule
              vehicles={selectorVehicles}
              onAddBooking={handleAddBooking}
              onUpdateBooking={handleUpdateBooking}
              onUpdateBookingStatus={handleUpdateBookingStatus}
              onApplyStatus={applyBookingStatus}
              currency={agency.currency}
              locations={locations}
              whatsappTemplate={agency.whatsappTemplate}
              agencyName={agency.shortName}
              requestCreate={createRequest?.kind === 'booking' ? createRequest.token : 0}
            />
          )}

          {activeSection === 'customers' && (
            <ClientsModule currency={agency.currency} canDelete={canDelete} requestCreate={createRequest?.kind === 'client' ? createRequest.token : 0} />
          )}

          {activeSection === 'fleet' && (
            <FleetModule
              onAddVehicle={handleAddVehicle}
              onUpdateVehicle={handleUpdateVehicle}
              onUpdateVehicleStatus={handleUpdateVehicleStatus}
              onApplyStatus={applyVehicleStatus}
              onDeleteVehicle={handleDeleteVehicle}
              canDelete={canDelete}
              currency={agency.currency}
              locations={locations}
            />
          )}

          {activeSection === 'handover' && (
            <div className="space-y-6">
              <ContractsPanel bookings={searchedBookings} />
              <HandoverModule
              bookings={searchedBookings}
              vehicles={selectorVehicles}
              clients={directoryClients}
              onUpdateBooking={handleUpdateBooking}
              onUpdateVehicle={handleUpdateVehicle}
              agency={agency}
              currency={agency.currency}
            />
            </div>
          )}

          {activeSection === 'maintenance' && (
            <FleetModule
              onAddVehicle={handleAddVehicle}
              onUpdateVehicle={handleUpdateVehicle}
              onUpdateVehicleStatus={handleUpdateVehicleStatus}
              onApplyStatus={applyVehicleStatus}
              onDeleteVehicle={handleDeleteVehicle}
              canDelete={canDelete}
              maintenanceOnly
              currency={agency.currency}
              locations={locations}
            />
          )}

          {activeSection === 'tarifs' && (
            <TariffsModule
              vehicles={selectorVehicles}
              rules={tariffRules}
              setRules={setTariffRules}
              tiers={discountTiers}
              setTiers={setDiscountTiers}
              extras={extras}
              setExtras={setExtras}
              currency={agency.currency}
              headerQuery={searchQuery}
            />
          )}

          {activeSection === 'caisse' && (
            <CashModule currency={agency.currency} headerQuery={searchQuery} />
          )}

          {activeSection === 'amendes' && (
            <FinesModule vehicles={selectorVehicles} bookings={handoverBookings} clients={directoryClients} currency={agency.currency} headerQuery={searchQuery} />
          )}

          {activeSection === 'analytics' && (
            <FinanceModule
              bookings={handoverBookings}
              canViewProfits={canViewProfits}
              currency={agency.currency}
              headerQuery={searchQuery}
            />
          )}

          {activeSection === 'agence' && (
            <AgencyModule
              agency={agency}
              setAgency={setAgency}
              backupData={backupData}
              onImport={handleImport}
              jsonImportAllowed={jsonImportAllowed && role === 'super_admin'}
            />
          )}

          {activeSection === 'staff' && <StaffModule canManage={role === 'super_admin'} />}
          {activeSection === 'profile' && <OwnProfile platform={false} />}

          {activeSection === 'settings' && (
            <SettingsModule
              role={role}
              onResetDemo={handleResetDemo}
            />
          )}
            </>
          )}

          {searchQuery && (
            <p className="mt-4 break-all text-[11px] text-muted">
              Global search “{searchQuery}” is applied to the open section.
            </p>
          )}
        </main>
      </div>
      <CommandPalette
        open={paletteOpen}
        entries={commandEntries}
        onClose={() => setPaletteOpen(false)}
        onGo={openCommand}
      />
      <LogoutModal open={logoutOpen} onClose={() => setLogoutOpen(false)} />
    </div>
    </DeskToastProvider>
  );
}

function deskRoleLabel(role: StaffRole, impersonating: boolean): string {
  if (impersonating) return 'Platform, this agency';
  if (role === 'super_admin') return 'Agency owner';
  return 'Front desk';
}

function isDeskSection(value: string): value is AdminSidebarSection {
  return (
    value === 'dashboard' ||
    value === 'bookings' ||
    value === 'customers' ||
    value === 'fleet' ||
    value === 'handover' ||
    value === 'maintenance' ||
    value === 'tarifs' ||
    value === 'caisse' ||
    value === 'amendes' ||
    value === 'analytics' ||
    value === 'agence' ||
    value === 'staff' ||
    value === 'profile' ||
    value === 'settings'
  );
}
